import { API_CONFIG, getAuthHeaders } from '../config/api';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
}

const AUTH_ENDPOINTS_WITH_EXPECTED_401 = ['/auth/login', '/auth/register', '/auth/driver-register', '/auth/forgot-password', '/auth/send-otp', '/auth/verify-otp'];

export const SESSION_EXPIRED_EVENT = 'app:session-expired';
export const SESSION_EXPIRED_MESSAGE_KEY = 'sessionExpiredMessage';

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

class ApiService {
  private baseUrl: string;
  private sessionExpiryFired = false;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  private handleUnauthorized(endpoint: string, message?: string) {
    const normalized = endpoint.split('?')[0];
    if (AUTH_ENDPOINTS_WITH_EXPECTED_401.includes(normalized)) return;
    if (this.sessionExpiryFired) return;
    this.sessionExpiryFired = true;

    try {
      sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, message || 'Oturum süresi doldu, tekrar giriş yapın');
    } catch {
      // storage erişilemezse sessizce geç
    }

    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }

  private createTimeoutController(timeoutMs: number) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      controller.abort(new DOMException('Request timed out', 'TimeoutError'));
    }, timeoutMs);
    return {
      signal: controller.signal,
      clear: () => window.clearTimeout(timer),
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requestOptions: { timeoutMs?: number; signal?: AbortSignal } = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    };

    try {
      const timeout = this.createTimeoutController(requestOptions.timeoutMs ?? API_CONFIG.TIMEOUT);
      requestOptions.signal?.addEventListener('abort', timeout.clear, { once: true });
      const signal = requestOptions.signal ? AbortSignal.any([timeout.signal, requestOptions.signal]) : timeout.signal;
      let response: Response;
      try {
        if (requestOptions.signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
        response = await fetch(url, {
          ...options,
          headers,
          signal,
        });
      } finally {
        timeout.clear();
        requestOptions.signal?.removeEventListener('abort', timeout.clear);
      }

      const rawBody = await response.text();
      let data: any = {};
      if (rawBody) {
        try {
          data = JSON.parse(rawBody);
        } catch {
          if (!response.ok) {
            const compactBody = rawBody.replace(/\s+/g, ' ').slice(0, 300);
            throw new ApiError(`JSON olmayan yanıt döndü: ${compactBody}`, response.status, rawBody);
          }
          throw new ApiError('Sunucu JSON yerine beklenmeyen bir yanıt döndü', response.status, rawBody);
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          this.handleUnauthorized(endpoint, data.message);
        }
        throw new ApiError(data.message || 'API request failed', response.status, data);
      }

      return data;
    } catch (error) {
      if ((error as any)?.name === 'AbortError' && requestOptions.signal?.aborted) {
        throw new ApiError('İstek iptal edildi', 499, { endpoint });
      }
      if ((error as any)?.name === 'AbortError' || (error as any)?.name === 'TimeoutError') {
        throw new ApiError('Sunucu yanit vermedi (zaman asimi)', 408, { endpoint });
      }
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new ApiError('Sunucu yanit vermedi (zaman asimi)', 408, { endpoint });
      }
      if (error instanceof TypeError) {
        throw new ApiError('Sunucuya baglanilamadi', 0, { endpoint, cause: error.message });
      }
      console.error('API Error:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string, requestOptions: { timeoutMs?: number; signal?: AbortSignal } = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' }, requestOptions);
  }

  async post<T>(endpoint: string, data?: any, requestOptions: { timeoutMs?: number; signal?: AbortSignal } = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }, requestOptions);
  }

  async put<T>(endpoint: string, data?: any, requestOptions: { timeoutMs?: number; signal?: AbortSignal } = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, requestOptions);
  }

  async delete<T>(endpoint: string, requestOptions: { timeoutMs?: number; signal?: AbortSignal } = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' }, requestOptions);
  }
}

export const apiService = new ApiService();
